import { useEffect, useState } from "react";

// Layouts
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// MUI
import { Grid, Divider } from "@mui/material";

// Icons
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";

// Components
import LargeButton from "@/Layouts/components/Buttons/LargeButton";
import BonusDepartments from "@/PayPlans/components/Commons/BonusDepartments";
import OperationCodes from "@/PayPlans/components/Commons/OperationCodes";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// Types
import {
	Content,
	UseSectionsErrorsReturn,
	OnActionUpdateTabParams,
	TargetedPerformanceBonusContent,
	ModelKeys,
	IOpCode,
} from "../../../../types/sections/technicians";

import { DEFAULT_TABS, DEFAULT_KEYS, EMPTY_MESSAGE } from "./constants";

import {
	isDisabledAdd,
	generateEmptyDepartmentsModel,
	generateEmptyOperationsModel,
	generateInnerModel,
	isValidModels,
	areAllEmpty,
} from "./utils";

type TargetedPerformanceBonusProps = {
	sectionId: string;
	content?: Content;
	loading: boolean;
	readOnly: boolean;
	useSectionsErrors?: () => UseSectionsErrorsReturn;
	onActionUpdate: (sectionId: string, fill: any, modelToPropagate: any, isValid: boolean) => void;
};

export default function TargetedPerformanceBonus(props: TargetedPerformanceBonusProps) {
	const {
		sectionId = "targetedPerformanceBonus",
		content = null,
		loading = false,
		readOnly = false,
		useSectionsErrors = () => ({}) as UseSectionsErrorsReturn,
		onActionUpdate,
	} = props;

	// Hooks
	const { addSectionError, removeSectionError } = useSectionsErrors?.();

	// States
	const [innerModels, setInnerModels] = useState<Content>(generateInnerModel());
	const [tabIndex, setTabIndex] = useState<number>(0);
	const [isDepsValid, setIsDepsValid] = useState(false);
	const [isOpCodesValid, setIsOpCodesValid] = useState(false);
	const [disabledDepartments, setDisabledDepartments] = useState<string[]>([]);
	const [externalOpCodes, setExternalOpCodes] = useState<IOpCode[]>([]);

	const onTabChange = (index: number) => {
		setTabIndex(index);
	};

	useEffect(() => {
		if (content) {
			const modelUpdated: Content = generateInnerModel(content);
			setInnerModels({ ...modelUpdated });
			updateDepAndCodes(modelUpdated);
			const { validDeps, validOpCodes } = isValidModels(modelUpdated);
			setIsDepsValid(validDeps);
			setIsOpCodesValid(validOpCodes);
			updateDepAndCodes(modelUpdated);

			if (!content.departments?.length && content.operationCodes?.length) {
				setTabIndex(1);
			}
		}

		return () => {
			setInnerModels({ ...generateInnerModel() });
		};
	}, [content]);

	const handleAddModel = (key: ModelKeys) => {
		const updatedModels = innerModels;
		switch (key) {
			case "departments":
				updatedModels[key].push(generateEmptyDepartmentsModel());
				break;
			case "operationCodes":
				updatedModels[key].push(generateEmptyOperationsModel());
				break;
		}
		setInnerModels({ ...updatedModels });
		updateDepAndCodes(updatedModels);
		const { validDeps, validOpCodes } = isValidModels(updatedModels);
		setIsDepsValid(validDeps);
		setIsOpCodesValid(validOpCodes);
	};

	useEffect(() => {
		if (!isDepsValid || !isOpCodesValid || areAllEmpty(innerModels)) {
			addSectionError(sectionId);
		} else {
			removeSectionError(sectionId);
		}
	}, [isOpCodesValid, isDepsValid]);

	const updateDepAndCodes = (tabsUpdated: Content) => {
		let disabledDeps: string[] = [];
		tabsUpdated[DEFAULT_KEYS.DEPARTMENTS_KEY].forEach((dep: TargetedPerformanceBonusContent) => {
			disabledDeps = disabledDeps.concat(dep.selectedDepartment?.length ? dep.selectedDepartment : []);
		});
		setDisabledDepartments([...disabledDeps]);

		let disabledCodes: IOpCode[] = [];
		tabsUpdated[DEFAULT_KEYS.OPERATION_CODES_KEY].forEach((op: TargetedPerformanceBonusContent) => {
			disabledCodes = disabledCodes.concat(
				op.selectedOperationCodes?.length
					? op.selectedOperationCodes.map((code: IOpCode) => ({
							code: code.code,
							type: code.type,
							pay: code.pay,
						}))
					: []
			);
		});
		setExternalOpCodes([...disabledCodes]);
	};

	const onActionUpdateTab = (params: OnActionUpdateTabParams) => {
		const { tabKey, index, modelToPropagate, isValid } = params;

		const tabsUpdated: Content = { ...innerModels };
		tabsUpdated[tabKey][index] = modelToPropagate;
		tabsUpdated[tabKey][index].isValid = isValid;
		setInnerModels({ ...tabsUpdated });
		updateDepAndCodes(tabsUpdated);
		const { validDeps, validOpCodes } = isValidModels(tabsUpdated);
		setIsDepsValid(validDeps);
		setIsOpCodesValid(validOpCodes);

		if (validDeps && validOpCodes) {
			sendActionUpdate(tabsUpdated);
		}
	};

	const sendActionUpdate = (updatedModels: Content) => {
		const departments: TargetedPerformanceBonusContent[] =
			updatedModels[DEFAULT_KEYS.DEPARTMENTS_KEY].map((dep: TargetedPerformanceBonusContent) => ({
				tiers: dep.tiers ?? [],
				applyTiersRetroactively: dep.applyTiersRetroactively ?? false,
				selectedDepartment: dep.selectedDepartment ?? [],
				selectedPayType: dep.selectedPayType ?? [],
				component: dep.component,
			})) ?? [];

		const operationCodes: TargetedPerformanceBonusContent[] =
			updatedModels[DEFAULT_KEYS.OPERATION_CODES_KEY].map((opCodes: TargetedPerformanceBonusContent) => ({
				tiers: opCodes.tiers ?? [],
				applyTiersRetroactively: opCodes.applyTiersRetroactively ?? false,
				selectedPayType: opCodes.selectedPayType ?? [],
				selectedOperationCodes: opCodes.selectedOperationCodes ?? [],
				selectedTimeLapse: opCodes.selectedTimeLapse ?? "",
				component: opCodes.component,
			})) ?? [];

		const modelToSave: Content = {
			departments: departments,
			operationCodes: operationCodes,
		};
		onActionUpdate(sectionId, null, modelToSave, true);
	};

	const handleDeleteModel = (index: number, key: string) => {
		const updatedModels = { ...innerModels };
		switch (key) {
			case "departments":
				updatedModels[key].splice(index, 1);
				break;
			case "operationCodes":
				updatedModels[key].splice(index, 1);
				break;
		}
		setInnerModels({ ...updatedModels });
		updateDepAndCodes(updatedModels);
		const { validDeps, validOpCodes } = isValidModels(updatedModels);
		setIsDepsValid(validDeps);
		setIsOpCodesValid(validOpCodes);
		if (validDeps && validOpCodes) {
			sendActionUpdate(updatedModels);
		}
	};

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader tabs={2} switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					<TabSelectable
						tabs={Object.values(DEFAULT_TABS).map((tabName) => tabName)}
						tabSelected={tabIndex}
						onTabChange={onTabChange}
						errors={[!isDepsValid, !isOpCodesValid]}
						style={{ marginLeft: "32px" }}
						generalProps={{
							ml: "0px",
							mr: "0px",
						}}
						content={[
							<Grid container key={`deps`}>
								{innerModels.departments.map((inner: TargetedPerformanceBonusContent, index: number) => (
									<Grid item xs={12} className={"models-tiered-area"} key={`deps-${index}`}>
										<BonusDepartments
											sectionId={sectionId}
											tabKey={"departments"}
											index={index}
											content={inner}
											readOnly={readOnly}
											onActionUpdate={onActionUpdateTab}
											onActionDelete={() => handleDeleteModel(index, "departments")}
											disabledSelector={true}
											disabledDepartments={disabledDepartments}
										/>
										{index < innerModels.departments.length - 1 && <Divider variant="middle" className="!mt-10" />}
									</Grid>
								))}
								{areAllEmpty(innerModels) && (
									<Grid container justifyContent={"center"} mt={1}>
										<TierAlertError text={EMPTY_MESSAGE} />
									</Grid>
								)}
								<Grid className={"large-button-container"} item xs={12}>
									<LargeButton
										disabled={
											isDisabledAdd({ isValidModels: isDepsValid, innerModels: innerModels?.departments }) || readOnly
										}
										className={"btn-primary"}
										text="Add Departments and Pay Types"
										onClick={() => {
											handleAddModel(DEFAULT_KEYS.DEPARTMENTS_KEY);
										}}
										endIcon={<AddCircleRoundedIcon />}
									/>
								</Grid>
							</Grid>,
							<Grid container key={`opCodes`}>
								{innerModels.operationCodes.map((inner: TargetedPerformanceBonusContent, index: number) => (
									<Grid item xs={12} className={"models-tiered-area"} key={`opCodes-${index}`}>
										<OperationCodes
											sectionId={sectionId}
											tabKey={"operationCodes"}
											index={index}
											content={inner}
											readOnly={readOnly}
											onActionUpdate={onActionUpdateTab}
											onActionDelete={() => handleDeleteModel(index, "operationCodes")}
											disabledSelector={true}
											externalOpCodes={externalOpCodes}
										/>
										{index < innerModels.operationCodes.length - 1 && <Divider variant="middle" className="!mt-10" />}
									</Grid>
								))}
								{areAllEmpty(innerModels) && (
									<Grid container justifyContent={"center"} mt={1}>
										<TierAlertError text={EMPTY_MESSAGE} />
									</Grid>
								)}
								<Grid className={"large-button-container"} item xs={12}>
									<LargeButton
										disabled={
											isDisabledAdd({ isValidModels: isOpCodesValid, innerModels: innerModels?.operationCodes }) ||
											readOnly
										}
										className={"btn-primary"}
										text="Add Operation Codes"
										onClick={() => {
											handleAddModel(DEFAULT_KEYS.OPERATION_CODES_KEY);
										}}
										endIcon={<AddCircleRoundedIcon />}
									/>
								</Grid>
							</Grid>,
						]}
					/>
				)}
			</Grid>
		</Grid>
	);
}
